import { RouteRecordRaw, RouteRecord } from 'vue-router'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useCmsStore } from '@/js/stores/cmsStore'
import { useProductStore } from '@/js/stores/productStore'
import type { IRogue } from '@/js/services/RogueTypes'

type RouteResp = {
  entityId?: number;
  entityType?: 'product' | 'category' | 'cms' | 'page';
  sku?: string;
  requestPath?: string;
  redirectTarget?: string;
  redirectType?: number;
  related?: {
    AUSTRALIA?: string;
    EUROPE?: string;
    US?: string;
    CANADA?: string;
    UK?: string;
  };
};

// We have some categories where we want to instead load a different CMS page...
// This should be deprecated and moved into roguebuilder. For now, when we want to show CategoryID, instead show CMS
// page (value). But keep the URL the same as the category URL.
const STATIC_CATEGORY_PAGES: { [key: number]: string } = {
  4691 : 'storage-landing',
  6043 : 'facility-outfitting-landing',
  6069 : 'garage-gyms-landing',
  6071 : 'affiliate-gyms-landing',
  6075 : 'training-gyms-landing',
}

const CategoryComponent = () => import(
  /* webpackChunkName: "pf-category" */ '@/js/pages/category/category.vue'
)
const CategoryFallback = () => import(
  /* webpackChunkName: "pf-category-fallback" */ '@/js/pages/category/fallback.vue'
)
const CmsComponent = () => import(/* webpackChunkName: "pf-category" */ '@/js/pages/cms/cms.vue')
const PdpComponent = () => import(/* webpackChunkName: "pf-product" */ '@/js/pages/product/product.vue')
const errComponent = () => import('@/js/pages/404/404.vue')

export async function loadRoute(
  path: string,
  rogue: IRogue,
  router = rogue.router,
  recursionCount = 0
): Promise<RouteRecordRaw | RouteRecord> {
  // @todo this is a safety switch for infinite recursion. Figure out why the route is loading multiple times...
  if (rogue.ssrContext?.timedOut) {
    const errRoute = {
      path,
      name      : `Error: Not Found (${path})`,
      component : errComponent,
      meta      : {
        status: 404,
      },
    }

    router?.addRoute(errRoute)
    if (rogue.ssrContext.renderData) {
      rogue.ssrContext.renderData.cache.disable = true
    }

    return errRoute
  }

  if (typeof path !== 'string') {
    rogue.log.warn('Tried to load a route that was not a string', path)

    // @ts-ignore
    path = path?.path || path?.url
  }

  if (path.indexOf('?') !== -1) {
    path = path.split('?')[0]
  }
  if (path.indexOf('#') !== -1) {
    path = path.split('#')[0]
  }

  if (rogue.l10n.path && path.startsWith(rogue.l10n.path)) {
    path = path
      .replace(new RegExp(`^${rogue.l10n.path}/`, 'i'), '/')
      .replace(new RegExp(`^${rogue.l10n.path}$`, 'i'), '')
  }

  // Check if the route is already loaded. If so we dont need to load it again
  const match = router?.resolve(path)?.matched?.[0]
  if (match) {
    return match
  }

  // rogue.log.debug(`Checking route for ${path}`)

  // Check if this route is already known as tied to a specific entity. If so we can add it to the router
  const categoryStore = useCategoryStore(rogue.pinia)
  const cmsStore = useCmsStore(rogue.pinia)
  const productStore = useProductStore(rogue.pinia)
  let productId = productStore.byUrl[path]
  let categoryId = categoryStore.byUrl[path]
  let cmsId = cmsStore.byUrl[path] as string | number | undefined

  // See the note above...
  if (categoryId && STATIC_CATEGORY_PAGES[categoryId]) {
    cmsId = STATIC_CATEGORY_PAGES[categoryId]
    categoryId = undefined
  }

  let routeData: RouteResp = {}
  let categoryIdFromRouteService = false

  if (!productId && !categoryId && !cmsId) {
    rogue.log.debug(`Route not found in stores for ${path}, checking net...`)
    try {
      routeData = await rogue.api.fetch<RouteResp>({
        url    : '/svc_route/route',
        // Cache this if we are in SSR mode
        cache  : { key: `route-${path}`, seconds: 60 * 60 * 2 },
        params : { url: path },
      })

      rogue.routeResp = routeData

      if (routeData.redirectTarget && routeData.redirectType) {
        if (routeData.redirectTarget.startsWith('http') && !rogue.ssr && typeof window !== 'undefined' && (rogue.l10n.path || routeData.redirectTarget.indexOf(window.location.hostname) === -1)) {
          window.location.replace(routeData.redirectTarget)
        }

        if (!routeData.redirectTarget.startsWith('/')) {
          routeData.redirectTarget = `/${routeData.redirectTarget}`
        }

        const redirect: RouteRecordRaw = {
          path,
          redirect : routeData.redirectTarget,
          meta     : {
            redirect : routeData.redirectTarget,
            status   : routeData.redirectType,
          },
        }
        router?.addRoute(redirect)

        return redirect
      }

      if (routeData.entityType === 'category') {
        categoryId = routeData.entityId
        categoryIdFromRouteService = true

        if (categoryId && STATIC_CATEGORY_PAGES[categoryId]) {
          cmsId = STATIC_CATEGORY_PAGES[categoryId]
          categoryId = undefined
          categoryIdFromRouteService = false
        }
      } else if (routeData.entityType === 'product') {
        productId = routeData.entityId || null
      } else if (
        routeData.entityType === 'cms'
        || routeData.entityType === 'page'
      ) {
        cmsId = routeData.entityId
      }
    } catch (e) {
      if (!rogue.ssr) {
        rogue.log.warn(`Tried to load nonexistant route ${path}`)
      }
      const errRoute = {
        path,
        name      : `404 ${path}`,
        component : errComponent,
        meta      : {
          status: 404,
        },
      }
      router?.addRoute(errRoute)

      return errRoute
    }
  }

  if (productId) {
    // We may know the product Id but no actual data about the product yet - e.g. if the link had productid="123" we know its
    // product 123 but its better to load the actual product first.
    if (!productStore.products[productId]) {
      await productStore.loadAllChildren(productId, rogue)
    }

    // Check to see if the product is loaded now.
    const product = productStore.products[productId]

    if (!product) {
      // Had a route saved for a product, but it was NOT what we thought it was. Lets remove the route and recall
      // the function so that we can check the route service for a better route.

      productStore.eraseIdForUrl(path)

      if (recursionCount < 2) {
        return loadRoute(path, rogue, router, recursionCount + 1)
      }

      rogue.log.warn(`Recursion count reached loading product ${path} for ${rogue.site.code}`)
    }

    rogue.log.debug(`Adding route for product ${product?.name} ${path}`)
    const pdpRoute: RouteRecordRaw = {
      path,
      name      : `PDP: ${product?.name || 'Not Found'} (${productId})`,
      component : product ? PdpComponent : errComponent,
      // Swapping in errComponent is a 404 - product.vue normally reports that
      // itself, and it does not render at all here.
      meta      : { type: 'product', id: productId, ...(product ? {} : { status: 404 }) },
    }

    if (rogue.ssr && routeData?.related && pdpRoute.meta) {
      pdpRoute.meta.related = routeData.related
    }

    router?.addRoute(pdpRoute)

    return pdpRoute
  }

  if (categoryId) {
    // Load category if not already loaded
    if (!categoryStore.categories[categoryId]) {
      await categoryStore.load(categoryId, rogue)
    }

    const category = categoryStore.categories[categoryId]

    if (!category) {
      categoryStore.eraseIdForUrl(path)

      if (categoryIdFromRouteService) {
        rogue.log.warn(`Route service returned invalid category ID ${categoryId} for ${path} - returning 404`)
        const errRoute = {
          path,
          name      : `404 ${path}`,
          component : errComponent,
          meta      : {
            status: 404,
          },
        }
        router?.addRoute(errRoute)
        
        return errRoute
      }

      if (recursionCount < 2) {
        return loadRoute(path, rogue, router, recursionCount + 1)
      }
      rogue.log.warn(`Recursion count reached loading category ${path} ${rogue.site.code}`)
    }

    rogue.log.debug(`Adding route for category ${category?.name} ${path}`)

    // Preload the category's translation namespace here, before the component renders.
    // The category page computes its <title>/meta description synchronously in setup via
    // rogue.__(..., category.name); if this namespace isn't loaded yet (it isn't in
    // DEFAULT_NS), SSR emits the untranslated fallback and only the client picks up the
    // translation after hydration (EDEV-3495).
    if (category && rogue.i18n?.enabled) {
      const loadTranslations = rogue.i18n.loadNamespace(category.name)
      if (loadTranslations) {
        await loadTranslations
      }
    }

    let component = CategoryComponent

    if (rogue.featureFlags.isOn('rfk_fallback')) {
      component = CategoryFallback
    }

    const categoryRoute: RouteRecordRaw = {
      path,
      name : `Category: ${category?.name || 'Not Found'} (${categoryId})`,
      component,
      meta : { type: 'category', id: categoryId },
    }

    if (rogue.ssr && routeData?.related && categoryRoute.meta) {
      categoryRoute.meta.related = routeData.related
    }

    router?.addRoute(categoryRoute)

    return categoryRoute
  }

  if (cmsId) {
    // Load the CMS page if it is not already
    const cmsObj = cmsStore.cms[cmsId] || (await cmsStore.load(cmsId, rogue))

    if (!cmsObj) {
      // If we still dont have a CMS page, we need to clear out the (incorrect) map we have and load from net
      cmsStore.eraseIdForUrl(path)

      if (recursionCount < 2) {
        return loadRoute(path, rogue, router, recursionCount + 1)
      }

      rogue.log.warn(`Recursion count reached loading cms ${path}`)
    }

    rogue.log.debug(`Adding route for cms ${cmsObj?.identifier} ${path}`)

    const cmsRoute: RouteRecordRaw = {
      path,
      name      : `CMS: ${cmsObj ? cmsObj.identifier || cmsObj.id : `Not Found ${path}`}`,
      component : cmsObj ? CmsComponent : errComponent,
      // As above - the error component standing in for a page we couldn't load
      // is a 404, and cms.vue never runs to say so itself.
      meta      : { type: 'cms', id: cmsObj?.id, ...(cmsObj ? {} : { status: 404 }) },
    }

    if (rogue.ssr && routeData?.related && cmsRoute.meta) {
      cmsRoute.meta.related = routeData.related
    }

    router?.addRoute(cmsRoute)

    return cmsRoute
  }

  // We were not able to find a dynamic page for this route, or a redirect - so it is a 404
  // Escape colons so vue-router doesn't treat them as param markers (e.g. "https:" in a crawled URL)
  const safePath = path.replace(/:/g, '\\:')
  const errRoute = {
    path      : safePath,
    name      : `Error: Not Found (${path})`,
    component : errComponent,
    meta      : {
      status: 404,
    },
  }

  router?.addRoute(errRoute)

  return errRoute
}
